import { memo, useEffect, useState } from "react";

/**
 * Props for Children element.
 * @typedef
 * @name ChildrenProps
 * @kind variable
 */
type ChildrenProps = {
  /**
   * (optional)
   * Value for spinner size in px.
   * @default 100px
   * @property
   * @name size
   * @type {string | undefined}
   */
  size?: number;
  /**
   * `"primary"` for the orange logo with the white background,
   * `"secondary"` for the white logo with the orange background.
   * @default "primary"
   * @property
   * @name type
   * @type {"primary" | "secondary"}
   */
  type: "primary" | "secondary";
  /**
   * (optional)
   * If `true`, shows the spinner icon.
   * @property
   * @name showIcon
   * @type {boolean | undefined}
   */
  showIcon?: boolean;
  /**
   * (optional)
   * Stroke width.
   * @default "8"
   * @property
   * @name strokeWidth
   * @type {string | undefined}
   */
  strokeWidth?: string;
};

/**
 * Memoized Spinner Component.
 * @constant
 * @name SpinnerComponent
 * @kind variable
 * @type {React.NamedExoticComponent<ChildrenProps>}
 */
const SpinnerComponent: React.NamedExoticComponent<ChildrenProps> = memo(
  /**
   * Spinner Component.
   * If you need to adjust the logo position, please check
   * {@link https://yqnn.github.io/svg-path-editor/ | the official documentation}.
   * Convert to relative to update the Ms and convert back to
   * absolute when done or else the logo won't render properly.
   * @function
   * @name SpinnerComponent
   * @kind function
   * @memberof SpinnerComponent
   * @param {ChildrenProps} props Props for Children element.
   * @returns {JSX.Element} Spinner Component.
   */
  function SpinnerComponent(props: ChildrenProps): JSX.Element {
    const [rotation, setRotation] = useState(0);

    useEffect(() => {
      const rotate = () => {
        setRotation((rotation - 3) % 360); // Rotation direction
      };

      const rotationInterval = setInterval(rotate, 5); // Rotation speed

      return () => {
        clearInterval(rotationInterval);
      };
    }, [rotation]);

    const type = props.type ? props.type : "primary";
    const size = props.size ? props.size : "80px"; // Change default size to 80px
    const colors =
      type === "primary"
        ? {
            first: "rgb(218, 43, 30)",
            second: "rgb(237, 150, 144)",
            third: "rgba(255, 255, 255, 0)",
          }
        : {
            first: "rgb(255, 255, 255)",
            second: "rgb(238, 154, 148)",
            third: "rgba(221, 60, 48)",
          };

    // We pass the rotation on the conicGradient to make the colors change position
    const linearGradient = `linear-gradient(to top, ${colors.first} 0%, ${colors.second} 50%, ${colors.third} 100%)`;
    const conicGradient = `conic-gradient(from ${rotation}deg, ${colors.first} 0%, ${colors.second} 50%, ${colors.third} 100%)`;

    return (
      <div
        style={{
          width: `${size}px`, // Set the width and height to match the circle size
          height: `${size}px`, // Set the width and height to match the circle size
          borderRadius: "100%", // Make the parent div round
          overflow: "hidden", // Clip the SVG to the parent div's rounded shape
        }}
      >
        <svg width={size} height={size} viewBox="0 0 100 100">
          <circle
            r="50"
            cx="50"
            cy="50"
            fill={`${type === "primary" ? "white" : "#da291c"}`}
          />
          {props.showIcon ? (
            <g transform={`translate(24, 24) scale(0.8) `}>
              <path
                d="M60.1362 32.9016C60.1172 32.7592 60.0887 32.6264 60.0602 32.4841C59.8702 31.5543 55.9843 22.4363 49.9987 8.06189C49.7612 7.55903 49.5046 7.12258 49.2481 6.71459C47.4809 3.94409 45.0772 3.612 42.1034 5.72784C40.6878 6.73357 39.3577 7.84367 37.98 8.89684C37.1439 9.53254 36.3174 10.1777 35.4813 10.8134C34.4552 11.6009 33.4291 12.3979 32.3935 13.1854C31.1679 14.1247 29.9422 15.0641 28.7166 16.0034C27.2915 17.0945 25.8758 18.1856 24.4507 19.2767C22.8355 20.5197 21.2204 21.7531 19.6052 22.9961C18.5031 23.8405 17.3725 24.647 16.3084 25.5294C14.2942 27.1803 12.09 28.8691 9.68627 29.9128C8.35614 30.4916 6.8835 30.8142 5.44886 30.6149C3.90971 30.4062 2.48457 29.5713 1.51548 28.3663C1.01193 27.7401 0.764906 27.1898 0.774407 26.3833C0.793409 25.4819 1.16394 24.609 1.61049 23.8405C-0.432213 26.6679 -0.536722 30.0741 1.32546 34.0781C1.32546 34.0781 2.38006 36.3552 4.54627 40.9095C5.73389 43.0443 7.48205 44.2303 9.80978 44.5054C12.6126 44.5434 15.3108 43.9077 17.9046 42.5509C18.4936 42.2378 19.0447 41.8962 19.6242 41.5262C25.9994 36.7252 31.3009 32.3797 35.5953 28.5086C33.7521 24.6375 32.8495 22.6829 32.8495 22.6829C40.1272 23.7171 44.4216 24.3149 45.6853 24.5046C45.9418 24.5236 46.0748 24.5711 46.0748 24.5711C46.0463 24.7039 46.0083 24.8083 45.9798 24.9316L45.9513 24.8557C45.1152 27.9109 44.2031 30.9375 43.3005 33.9642C42.7305 35.8713 42.1604 37.7784 41.5904 39.676C39.7187 35.8049 38.7496 33.8978 38.7496 33.8978C30.9778 40.5584 26.1704 44.5813 24.3462 45.9381C23.1966 46.7541 22.0945 47.3993 21.0589 47.9116C21.0969 47.9686 21.1349 48.0255 21.1729 48.0919C21.1349 48.0445 21.0969 47.9875 21.0589 47.9401V47.9116C14.5697 51.1376 10.1803 48.8984 9.81928 44.5149C8.50816 47.5416 9.68627 51.8681 13.3726 57.5515C14.8358 59.8096 16.4224 61.859 18.8927 63.0925C21.6099 64.4587 24.6882 64.1836 27.4055 62.9312C31.1204 61.2233 55.5947 41.6116 55.5947 41.4218C59.2431 38.4236 60.6492 36.1939 60.1362 32.911V32.9016Z"
                fill={`${type === "primary" ? "#da291c" : "white"}`}
              />
              <path
                d="M0.785378 26.3918C0.766376 27.1983 1.0134 27.7486 1.51695 28.3748C2.48604 29.5798 3.92068 30.4147 5.45033 30.6235C6.88497 30.8227 8.35761 30.4906 9.68774 29.9214C12.0915 28.8872 14.2957 27.1888 16.3099 25.5379C17.374 24.6555 18.5141 23.849 19.6067 23.0046C21.2219 21.7617 22.837 20.5282 24.4522 19.2853C25.8773 18.1942 27.293 17.103 28.7181 16.0119C29.9437 15.0726 31.1693 14.1333 32.3949 13.194C33.421 12.4065 34.4566 11.6189 35.4827 10.8219C36.3188 10.1863 37.1454 9.54107 37.9815 8.90537C38.219 8.7251 38.4565 8.53533 38.6941 8.34557C38.295 7.60551 36.6799 3.57309 36.1573 2.33965C35.8248 1.56163 35.4542 0.726684 34.7227 0.299723C33.9056 -0.184167 32.8605 -0.0133823 31.9674 0.328187C30.2002 1.00184 28.7656 2.25426 27.3025 3.40231C25.5638 4.7591 23.8156 6.1064 22.0674 7.4537C18.5806 10.1578 15.0843 12.8619 11.5974 15.5565C9.78275 16.9607 7.96808 18.3649 6.1534 19.7692C4.54774 21.0121 2.79957 22.1412 1.61196 23.83C1.16541 24.5986 0.794881 25.4715 0.775879 26.3728L0.785378 26.3918ZM38.1715 7.47267L38.1905 7.50114L38.314 7.70039C38.219 7.53909 38.1145 7.37779 38.01 7.22599C38.067 7.30189 38.1145 7.37779 38.162 7.46319L38.1715 7.47267ZM23.7016 6.44797H23.7206C23.7206 6.44797 23.6921 6.44797 23.6826 6.44797H23.6636V6.46694C23.6636 6.46694 23.6636 6.46694 23.6541 6.46694L23.6161 6.49541C23.6161 6.49541 23.6446 6.47643 23.6636 6.46694L23.7016 6.44797ZM21.8774 7.66244C21.8774 7.66244 21.8964 7.65295 21.8964 7.64346L21.9344 7.615H21.9439C21.8584 7.67192 21.7729 7.73834 21.6874 7.79527L21.6684 7.81424C21.6684 7.81424 21.6684 7.81424 21.6589 7.81424H21.6684L21.8774 7.66244ZM14.5617 13.4501H14.5712V13.4312L14.6187 13.3837C14.6187 13.3837 14.6092 13.3837 14.5997 13.4027C14.5617 13.4406 14.5237 13.4881 14.4857 13.526L14.4667 13.545C14.4667 13.545 14.4762 13.5355 14.4857 13.526L14.5047 13.5071L14.5617 13.4501Z"
                fill={`${type === "primary" ? "#da291c" : "white"}`}
              />
            </g>
          ) : (
            ""
          )}
          <mask id="zenith-spinner-gradient">
            <circle
              r="47"
              cx="50"
              cy="50"
              stroke="#FFF"
              strokeWidth={props.strokeWidth || "8"}
              opacity="1"
            ></circle>
          </mask>
          <foreignObject
            x="0"
            y="0"
            width="100"
            height="100"
            strokeLinecap="round"
            mask="url(#zenith-spinner-gradient)"
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                background: linearGradient,
                backgroundImage: conicGradient,
                strokeLinecap: "round",
              }}
            ></div>
          </foreignObject>
        </svg>
      </div>
    );
  }
);

export default SpinnerComponent;
